import { useQueryClient } from '@tanstack/react-query';
import { ShieldOff } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api';
import { ustawWyjatekHr } from '@/lib/hrplus-api';
import { useI18n } from '@/lib/i18n';

/**
 * ZNACZNIK OSOBY WZGLĘDEM HR+ (12.10.2026) — lista pracowników i karta osoby.
 *
 * ⚠️ „Spoza HR+" OSTRZEGAWCZO: po włączeniu przełącznika blokowania osób spoza HR+ to ci ludzie
 * stracą dostęp, więc to ich trzeba obejrzeć przed włączeniem. Znacznik z serwera `null` (wyspa bez
 * ustawień HR+) nie rysuje niczego, a nieznany stan — sam kod, zamiast znikać po cichu.
 */
export function ZnacznikHr({ stan, numer }: { stan: string | null | undefined; numer?: string | null }) {
  const { t } = useI18n();
  if (!stan) return null;
  const variant = stan === 'SPOZA_HR' || stan === 'SPOZA_ZABLOKOWANA' ? 'warning' : stan === 'HR_PLUS' ? 'default' : 'neutral';
  const etykieta = t(`emp.hr.${stan}`);
  return (
    <Badge variant={variant} title={numer ? t('emp.hr.numer').replace('{n}', numer) : undefined}>
      {etykieta === `emp.hr.${stan}` ? stan : etykieta}
    </Badge>
  );
}

/**
 * WYJĄTEK OSOBY OD „Blokuj osoby spoza HR+" — przycisk na karcie pracownika (12.10.2026).
 *
 * ⚠️ TYLKO DLA OSOBY SPOZA HR+: powiązaną prowadzi HR+ (decyzja usera „b"), więc tam przycisku nie ma.
 * Po zmianie portal od razu przelicza pełną listą — zablokowana przez przełącznik zostaje odblokowana.
 */
export function WyjatekHrPrzelacznik({ groupId, employeeId, wyjatek }: { groupId: string; employeeId: string; wyjatek: boolean }) {
  const { t } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();
  const [pracuje, setPracuje] = useState(false);
  const przelacz = async () => {
    setPracuje(true);
    try {
      await ustawWyjatekHr(groupId, employeeId, !wyjatek);
      toast.success(t(wyjatek ? 'emp.hr.wyjatekZdjety' : 'emp.hr.wyjatekUstawiony'));
      await qc.invalidateQueries({ queryKey: ['employees'] });
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : t('common.error'));
    } finally {
      setPracuje(false);
    }
  };
  return (
    <Button size="sm" variant="outline" disabled={pracuje} onClick={() => void przelacz()}>
      <ShieldOff />
      {t(wyjatek ? 'emp.hr.zdejmijWyjatek' : 'emp.hr.ustawWyjatek')}
    </Button>
  );
}
